import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const SectionGlow: React.FC = () => {
  const { scrollYProgress } = useScroll();
  
  // Create different opacity values for different sections of the page
  const heroGlow = useTransform(scrollYProgress, [0, 0.15, 0.25], [0.8, 0.4, 0]);
  const featuresGlow = useTransform(scrollYProgress, [0.2, 0.3, 0.4], [0, 0.8, 0]);
  const testimonialsGlow = useTransform(scrollYProgress, [0.35, 0.45, 0.55], [0, 0.8, 0]);
  const challengesGlow = useTransform(scrollYProgress, [0.5, 0.6, 0.7], [0, 0.8, 0]);
  const pricingGlow = useTransform(scrollYProgress, [0.65, 0.75, 0.85], [0, 0.8, 0]);
  
  // Effect intensity based on scroll depth
  const glowSize = useTransform(
    scrollYProgress, 
    [0, 0.25, 0.5, 0.75, 1], 
    [300, 400, 500, 400, 300]
  );
  
  const glowIntensity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.6, 1],
    [0.15, 0.25, 0.2, 0.15]
  );
  
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Hero Section Glow */}
      <motion.div 
        className="absolute w-full h-full"
        style={{ opacity: heroGlow }}
      >
        <motion.div 
          className="absolute top-[30%] left-1/2 -translate-x-1/2 rounded-full blur-[80px]"
          style={{ 
            width: glowSize, 
            height: glowSize,
            background: `radial-gradient(circle, rgba(204, 255, 0, ${glowIntensity}) 0%, rgba(204, 255, 0, 0) 70%)`
          }}
        />
      </motion.div>
      
      {/* Features Section Glow */}
      <motion.div 
        className="absolute w-full h-full"
        style={{ opacity: featuresGlow }}
      >
        <motion.div 
          className="absolute top-[40%] left-1/2 -translate-x-1/2 rounded-full blur-[80px]"
          style={{ 
            width: glowSize, 
            height: glowSize,
            background: `radial-gradient(circle, rgba(204, 255, 0, ${glowIntensity}) 0%, rgba(204, 255, 0, 0) 70%)`
          }}
        />
      </motion.div>
      
      {/* Testimonials Section Glow */}
      <motion.div 
        className="absolute w-full h-full"
        style={{ opacity: testimonialsGlow }}
      >
        <motion.div 
          className="absolute top-[50%] left-1/2 -translate-x-1/2 rounded-full blur-[80px]"
          style={{ 
            width: glowSize, 
            height: glowSize,
            background: `radial-gradient(circle, rgba(204, 255, 0, ${glowIntensity}) 0%, rgba(204, 255, 0, 0) 70%)`
          }}
        />
      </motion.div>
      
      {/* Challenges Section Glow */}
      <motion.div 
        className="absolute w-full h-full"
        style={{ opacity: challengesGlow }}
      >
        <motion.div 
          className="absolute top-[60%] left-1/2 -translate-x-1/2 rounded-full blur-[80px]"
          style={{ 
            width: glowSize, 
            height: glowSize,
            background: `radial-gradient(circle, rgba(204, 255, 0, ${glowIntensity}) 0%, rgba(204, 255, 0, 0) 70%)`
          }}
        />
      </motion.div>
      
      {/* Pricing Section Glow */}
      <motion.div 
        className="absolute w-full h-full"
        style={{ opacity: pricingGlow }}
      >
        <motion.div 
          className="absolute top-[70%] left-1/2 -translate-x-1/2 rounded-full blur-[80px]"
          style={{ 
            width: glowSize, 
            height: glowSize,
            background: `radial-gradient(circle, rgba(204, 255, 0, ${glowIntensity}) 0%, rgba(204, 255, 0, 0) 70%)`
          }}
        />
      </motion.div>
      
      {/* Floating accent particles */}
      {Array.from({ length: 15 }).map((_, index) => (
        <motion.div
          key={`particle-${index}`}
          className="absolute rounded-full bg-accent"
          style={{
            width: Math.random() * 4 + 2, // 2-6px
            height: Math.random() * 4 + 2, // 2-6px
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: 0.2,
            filter: 'blur(1px)'
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.1, 0.3, 0.1],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: Math.random() * 5 + 8, // 8-13s
            repeat: Infinity,
            delay: Math.random() * 5
          }}
        />
      ))}
      
      {/* Scroll position indicator */}
      <motion.div 
        className="fixed right-4 top-1/2 -translate-y-1/2 w-1 h-[30vh] rounded-full bg-white/10 hidden md:block"
        style={{
          scaleY: 1,
        }}
      >
        <motion.div 
          className="absolute top-0 left-0 right-0 bg-accent rounded-full"
          style={{
            height: scrollYProgress.get() * 100 + '%',
            transition: 'height 0.1s'
          }}
        />
      </motion.div>
      
      {/* Section indicators */}
      {[0.05, 0.25, 0.45, 0.65, 0.85].map((position, index) => (
        <motion.div
          key={`indicator-${index}`}
          className="fixed right-4 w-2 h-2 rounded-full bg-white/30 hidden md:block"
          style={{
            top: `calc(50% - 15vh + ${position * 30}vh)`,
            scale: scrollYProgress.get() > position - 0.05 && scrollYProgress.get() < position + 0.05 ? 1.5 : 1,
            backgroundColor: scrollYProgress.get() > position - 0.05 && scrollYProgress.get() < position + 0.05 ? '#CCFF00' : 'rgba(255, 255, 255, 0.3)'
          }}
        />
      ))}
    </div>
  );
};

export default SectionGlow;